import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';
import type { HdeProfessionalExtraction } from '../utils/extract-professional-for-hde';
import { HDE_PROFESSIONAL_EXTRACTION_API_ROUTE } from '~/routes/remix-api.app.hde-professional-extraction';

interface ExtractionResponse {
    extraction: HdeProfessionalExtraction | null;
    error?: string;
}

/**
 * Fetches Professional data for HDE extraction when a business profile is selected.
 * Returns extracted CompanyType and ProjectLocation for pre-filling or skipping steps.
 * Caches extraction to avoid flicker when selectedUrlSegment briefly becomes null during re-renders.
 */
export function useHdeProfessionalExtraction(urlSegment: string | null) {
    const fetcher = useFetcher<ExtractionResponse>({
        key: 'hde-professional-extraction',
    });
    const loadRef = useRef(fetcher.load);
    loadRef.current = fetcher.load;
    const normalizedUrlSegment = urlSegment?.trim() ?? '';
    const lastRequestedUrlSegmentRef = useRef('');
    const lastExtractionRef = useRef<{
        urlSegment: string;
        extraction: HdeProfessionalExtraction | null;
    } | null>(null);

    useEffect(() => {
        if (!normalizedUrlSegment) {
            lastRequestedUrlSegmentRef.current = '';
            return;
        }
        if (lastRequestedUrlSegmentRef.current === normalizedUrlSegment) return;
        lastRequestedUrlSegmentRef.current = normalizedUrlSegment;

        const params = new URLSearchParams({
            urlSegment: normalizedUrlSegment,
        });
        loadRef.current(
            `${HDE_PROFESSIONAL_EXTRACTION_API_ROUTE}?${params.toString()}`,
        );
    }, [normalizedUrlSegment]);

    const data = fetcher.data;
    const isLoading = Boolean(normalizedUrlSegment) && fetcher.state !== 'idle';

    let extraction: HdeProfessionalExtraction | null;
    if (normalizedUrlSegment && !isLoading) {
        extraction = data?.extraction ?? null;
        lastExtractionRef.current = {
            urlSegment: normalizedUrlSegment,
            extraction,
        };
    } else if (
        normalizedUrlSegment &&
        isLoading &&
        lastExtractionRef.current?.urlSegment === normalizedUrlSegment
    ) {
        // Keep the previous extraction while the same profile is revalidating.
        extraction = lastExtractionRef.current.extraction;
    } else if (normalizedUrlSegment && isLoading) {
        extraction = null;
    } else {
        // urlSegment is null - return cached extraction to avoid flicker
        extraction = lastExtractionRef.current?.extraction ?? null;
    }

    return {
        extraction,
        isLoading,
        error: data?.error,
    };
}
